import { UserEntity } from '@backstage/catalog-model';

/**
 * What a Microsoft Teams chat is opened with: who is in it, and optionally its
 * name and a first message.
 */
export type TeamsChatOptions = {
    /** The people to chat with, by sign-in name - see {@link teamsChatUserOf}. */
    users: string[];
    /**
     * The name of the chat. Teams only takes it for a group chat - three people
     * or more, the one who opens it included - and ignores it otherwise.
     */
    topicName?: string;
    /**
     * A message put into the box, not sent: whoever opens the chat reads it,
     * changes it if need be, and sends it themselves.
     */
    message?: string;
};

/**
 * The deep link that opens a chat in Microsoft Teams - in the desktop app if
 * there is one, in the browser otherwise. Several users make a group chat.
 *
 * See https://learn.microsoft.com/microsoftteams/platform/concepts/build-and-test/deep-link-teams
 *
 * @public
 */
export function teamsChatUrl(options: TeamsChatOptions): string {
    // The users are separated by a bare comma, the way Microsoft documents
    // the link; everything else is encoded as usual.
    const users = options.users.map(encodeURIComponent).join(',');
    const params = new URLSearchParams();
    if (options.topicName) {
        params.set('topicName', options.topicName);
    }
    if (options.message) {
        params.set('message', options.message);
    }
    const rest = params.toString();
    return `https://teams.microsoft.com/l/chat/0/0?users=${users}${
        rest ? `&${rest}` : ''
    }`;
}

/**
 * Who a user is to Teams: their Entra sign-in name, which the Microsoft Graph
 * provider puts on the profile, or their email address, which is the same for
 * nearly everyone - an external account is where the two part.
 *
 * @public
 */
export function teamsChatUserOf(user: UserEntity): string | undefined {
    const profile = user.spec.profile as
        | { email?: string; userPrincipalName?: string }
        | undefined;
    return profile?.userPrincipalName ?? profile?.email;
}
